import { fetchChildDefectTypes } from '@/services/defect/defectType.repository';
import {
  findCellParentType,
  shouldUseCellDefaultForDefect
} from '@/services/defect/defectCode.utils';
import i18n from '@/plugins/i18n';

const state = {
  // 폼 데이터 (사용자 입력/선택)
  defectForm: {
    description: '',
    memo: '',
    status: 'FOUND',
    selectedMainType: null,
    selectedSubType: null
  },

  // UI 상태
  uiState: {
    resetFlag: false,
    isEdit: false,
    formValid: false
  },

  // 하위 결함 옵션 (동적 데이터)
  subDefectOptions: [],

  // 결함 상태 옵션 (고정 데이터)
  defectStatusOptions: [
    { code: 'FOUND', name: '결함발견' },
    { code: 'SCHEDULE', name: '조치예정' },
    { code: 'COMPLETE', name: '조치완료' }
  ]
};

const getters = {
  // 폼 데이터 getters
  getDefectDescription: state => state.defectForm.description,
  getDefectMemo: state => state.defectForm.memo,
  getDefectFormStatus: state => state.defectForm.status,
  getDefectFormMainType: state => state.defectForm.selectedMainType,
  getDefectFormSubType: state => state.defectForm.selectedSubType,

  // UI 상태 getters
  getResetFlag: state => state.uiState.resetFlag,
  getUiIsEdit: state => state.uiState.isEdit,
  getFormValid: state => state.uiState.formValid,

  // 하위 결함 옵션 getter
  getSubDefectOptions: state => state.subDefectOptions,

  // 결함 상태 옵션 getters
  getDefectStatusOptions: () => [
    { code: 'FOUND', name: i18n.t('defects.found') },
    { code: 'SCHEDULE', name: i18n.t('defects.actionPlanned') },
    { code: 'COMPLETE', name: i18n.t('defects.actionCompleted') }
  ],
  getDefaultDefectStatus: state => state.defectStatusOptions[0], // FOUND
  getDefaultDefectMainType: (state, getters, rootState) => {
    // cell 타입을 기본값으로 설정
    return rootState.defect.defectTypes.parentTypes.find(type => type.code === 'CELL') || null;
  },

  // 통합 상태 getter
  getDefectStatusForDisplay: (state, getters, rootState) => {
    const formStatus = state.defectForm.status;
    const serverStatus = rootState.defect.defectInfoDialog?.defect?.defectStatus;
    const defaultStatus = state.defectStatusOptions[0].code; // "FOUND"

    console.log('[DEFECT FORM STORE] getDefectStatusForDisplay:', {
      formStatus,
      serverStatus,
      defaultStatus,
      result: formStatus || serverStatus || defaultStatus
    });

    return formStatus || serverStatus || defaultStatus;
  },

  // 서버 전송용 데이터 getter
  getDefectDataForSave: state => {
    return {
      description: state.defectForm.description,
      memo: state.defectForm.memo,
      status: state.defectForm.status,
      selectedMainType: state.defectForm.selectedMainType,
      selectedSubType: state.defectForm.selectedSubType
    };
  }
};

// defectForm 초기화 함수
function initializeDefectForm(defect = null, rootState, defectStatusOptions = null) {
  // defectStatusOptions가 전달되지 않으면 기본값 사용
  const defaultStatus = defectStatusOptions?.[0] || { code: 'FOUND', name: i18n.t('defects.found') };

  // defect store에서 parentTypes 가져오기 (더 안전한 접근)
  const parentTypes = rootState?.defect?.defectTypes?.parentTypes || [];
  const cellMainType = findCellParentType(parentTypes);
  const defaultMainType = cellMainType;

  let selectedMainType = defect?.defectCode || defaultMainType;
  let selectedSubType = null;

  if (shouldUseCellDefaultForDefect(defect)) {
    selectedMainType = cellMainType || defaultMainType;
    selectedSubType = null;
  } else if (defect?.defectCode?.parent) {
    selectedMainType = defect.defectCode.parent;
    selectedSubType = defect.defectCode;
  }

  const description = shouldUseCellDefaultForDefect(defect)
    ? cellMainType?.description || ''
    : defect?.defectCode?.description || '';

  // 메모: 서버의 defectDesc 값 (사용자가 입력한 메모)
  const memo = defect?.defectDesc || '';

  const result = {
    description: description, // 결함 상세 설명
    memo: memo, // 메모 (서버의 defectDesc 값)
    status: defect?.defectStatus || defaultStatus.code,
    selectedMainType: selectedMainType,
    selectedSubType: selectedSubType
  };


  return result;
}

async function loadSubDefectOptions(commit, mainTypeId, context = '') {
  if (!mainTypeId) {
    console.warn(`[DEFECT FORM STORE] ${context} - mainTypeId가 없습니다.`);
    commit('UPDATE_SUB_DEFECT_OPTIONS', []);
    return;
  }

  try {
    const subOptions = await fetchChildDefectTypes(mainTypeId);
    commit('UPDATE_SUB_DEFECT_OPTIONS', subOptions);
  } catch (error) {
    console.warn(`[DEFECT FORM STORE] ${context} - 하위 결함 타입 로드 실패:`, error);
    commit('UPDATE_SUB_DEFECT_OPTIONS', []);
  }
}

// 초기 상위 타입 ID 가져오기 함수 (재사용 가능)
function getInitialMainTypeId(defect, rootState) {
  const serverDefect = defect || rootState?.defect?.defectInfoDialog?.defect;

  // defect store에서 parentTypes 가져오기 (더 안전한 접근)
  const parentTypes = rootState?.defect?.defectTypes?.parentTypes || [];

  // defectCode가 하위 타입인 경우 parent를 사용, 상위 타입인 경우 그대로 사용
  let initialMainType = serverDefect?.defectCode;
  if (initialMainType?.parent) {
    initialMainType = initialMainType.parent;
  }

  if (!initialMainType) {
    initialMainType = findCellParentType(parentTypes);
  }

  const result = initialMainType?.id || null;

  return result;
}

const mutations = {
  // 폼 데이터 mutations
  SET_DEFECT_DESCRIPTION(state, description) {
    state.defectForm.description = description;
  },
  SET_DEFECT_MEMO(state, memo) {
    state.defectForm.memo = memo;
  },
  SET_DEFECT_FORM_STATUS(state, status) {
    state.defectForm.status = status;
  },
  SET_DEFECT_FORM_MAIN_TYPE(state, mainType) {
    state.defectForm.selectedMainType = mainType;
  },
  SET_DEFECT_FORM_SUB_TYPE(state, subType) {
    state.defectForm.selectedSubType = subType;
  },

  // 하위 결함 옵션 mutation
  UPDATE_SUB_DEFECT_OPTIONS(state, options) {
    state.subDefectOptions = options;
  },

  // UI 상태 mutations
  SET_RESET_FLAG(state, value) {
    state.uiState.resetFlag = value;
  },
  SET_UI_IS_EDIT(state, value) {
    state.uiState.isEdit = value;
  },
  SET_FORM_VALID(state, value) {
    state.uiState.formValid = value;
  },

  // 통합 초기화
  RESET_DEFECT_FORM(state, rootState) {
    // 현재 서버 데이터 확인
    const serverDefect = rootState?.defect?.defectInfoDialog?.defect;

    // 초기화 함수를 사용하여 defectForm 설정
    state.defectForm = initializeDefectForm(serverDefect, rootState, state.defectStatusOptions);
    state.subDefectOptions = [];
    state.uiState.resetFlag = false;
    state.uiState.formValid = false;


  },

  // 서버 데이터로 초기화
  INITIALIZE_FROM_SERVER_DATA(state, defectFormData) {
    state.defectForm = defectFormData;
    state.subDefectOptions = [];
  }
};

const actions = {
  // 폼 필드 업데이트
  async updateDefectForm({ commit, dispatch }, { field, value }) {
    switch (field) {
    case 'description':
      commit('SET_DEFECT_DESCRIPTION', value);
      break;
    case 'memo':
      commit('SET_DEFECT_MEMO', value);
      break;
    case 'status':
      commit('SET_DEFECT_FORM_STATUS', value);
      break;
    case 'mainType':
      commit('SET_DEFECT_FORM_MAIN_TYPE', value);
      // 상위 타입 변경 시 하위 결함 리스트 업데이트
      await loadSubDefectOptions(commit, value?.id, 'mainType 변경');
      break;
    case 'subType':
      commit('SET_DEFECT_FORM_SUB_TYPE', value);
      break;
    default:
      console.warn('[DEFECT FORM STORE] 알 수 없는 필드:', field);
    }
  },

  // UI 상태 업데이트
  updateUiState({ commit }, { field, value }) {
    switch (field) {
    case 'resetFlag':
      commit('SET_RESET_FLAG', value);
      break;
    case 'isEdit':
      commit('SET_UI_IS_EDIT', value);
      break;
    case 'formValid':
      commit('SET_FORM_VALID', value);
      break;
    default:
      console.warn('[DEFECT FORM STORE] 알 수 없는 UI 필드:', field);
    }
  },

  // 폼 데이터 초기화
  async resetDefectFormData({ commit, rootState, dispatch }) {
    await dispatch('defect/loadParentDefectTypes', null, { root: true });
    commit('RESET_DEFECT_FORM', rootState);

    // 초기 상위 타입에 대한 하위 옵션 로드
    const initialMainTypeId = getInitialMainTypeId(null, rootState);
    await loadSubDefectOptions(commit, initialMainTypeId, 'resetDefectFormData');
  },

  // 서버 데이터로 초기화
  async initializeFromServer({ commit, rootState, state }, defect) {
    // action에서 initializeDefectForm을 호출하여 defectForm 데이터 생성
    const defectFormData = initializeDefectForm(defect, rootState, state.defectStatusOptions);
    commit('INITIALIZE_FROM_SERVER_DATA', defectFormData);

    // 초기 상위 타입에 대한 하위 옵션 로드
    const initialMainTypeId = getInitialMainTypeId(defect, rootState);
    await loadSubDefectOptions(commit, initialMainTypeId, 'initializeFromServer');
  },

  // 서버 전송용 데이터 준비
  prepareDefectDataForSave({ getters, rootState }) {
    const formData = getters.getDefectDataForSave;
    const serverDefect = rootState.defect.defectInfoDialog?.defect;

    return {
      id: serverDefect?.id,
      defectDesc: formData.memo, // 메모 내용을 defectDesc로 전송
      defectStatus: formData.status,
      defectCode: formData.selectedSubType?.id || formData.selectedMainType?.id,
      memo: formData.memo
      // 기타 서버 전송용 필드들...
    };
  },

  // 상위 결함 타입 선택 시 하위 결함 리스트 자동 업데이트
  async updateSelectedDefectType({ commit, state, dispatch }, {mainOrSub, Type}) {
    try{
      if(mainOrSub === 'main'){
        // 1. 상위 결함 타입 업데이트 및 하위 결함 타입 초기화
        commit('SET_DEFECT_FORM_MAIN_TYPE', Type);
        commit('SET_DEFECT_FORM_SUB_TYPE', null);

        // 2. 하위 결함 리스트 업데이트 (defectForm에서 관리)
        await loadSubDefectOptions(commit, Type?.id, 'updateSelectedDefectType');
      } else if(mainOrSub === 'sub'){
        // 1. 하위 결함 타입 업데이트
        commit('SET_DEFECT_FORM_SUB_TYPE', Type);
      }

      if (Type && Type.id) {
        // 3. 결함 타입 description 업데이트
        const { moduleDefectApi } = await import('../../../apis/module.defect.api');
        const description = await moduleDefectApi.getDefectTypeDescription(Type.id);
        commit('SET_DEFECT_DESCRIPTION', description);
      }
    }catch(error){
      console.warn('[DEFECT FORM STORE] 결함 타입 description 로드 실패:', error);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
